import { createContext, useContext } from 'react';

import { LoginValues } from '@remente/shared-frontend';
import { User } from 'firebase/auth';

export type { LoginValues };

export interface SignupValues {
  email: string;
  password: string;
  confirmPassword: string;
}

interface AuthContextBase {
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithFacebook: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  loading: boolean;
}

interface UnauthenticatedAuthContext extends AuthContextBase {
  user: User | null;
}

interface AuthenticatedAuthContext extends AuthContextBase {
  user: User;
}

type AuthContextType = AuthenticatedAuthContext | UnauthenticatedAuthContext;

export const AuthContext = createContext<AuthContextType>({
  user: null,
  login: async () => {},
  signup: async () => {},
  signInWithGoogle: async () => {},
  signInWithFacebook: async () => {},
  signInWithApple: async () => {},
  logout: async () => {},
  resetPassword: async () => {},
  loading: true,
});

export const useAuth = (): UnauthenticatedAuthContext => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const useAuthAuthenticated = (): AuthenticatedAuthContext => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthAuthenticated must be used within an AuthProvider');
  }
  if (!context.user) {
    throw new Error('useAuthAuthenticated must be used within an authenticated route');
  }
  return context as AuthenticatedAuthContext;
};
